import { z } from "zod";

export enum SeverityTier {
  MATCH = "MATCH",
  ACCEPTABLE = "ACCEPTABLE",
  MINOR = "MINOR",
  VARIANCE = "VARIANCE",
  CRITICAL = "CRITICAL",
  ERROR = "ERROR",
  SKIPPED_ZERO = "SKIPPED_ZERO",
  SKIPPED_REVERSAL = "SKIPPED_REVERSAL",
  SKIPPED_REVERSAL_TYPE = "SKIPPED_REVERSAL_TYPE",
  SKIPPED_INTERVENTION = "SKIPPED_INTERVENTION",
  SKIPPED_PRE_CONFIG = "SKIPPED_PRE_CONFIG",
}

export const SeverityTierSchema = z.nativeEnum(SeverityTier);

export interface SeverityTierConfig {
  label: string;
  color: string;
  backgroundColor: string;
  threshold: { min: number; max: number };
  description: string;
}

export const SEVERITY_TIER_CONFIG: Record<SeverityTier, SeverityTierConfig> = {
  [SeverityTier.MATCH]: {
    label: "Match",
    color: "#15803d",
    backgroundColor: "#dcfce7",
    threshold: { min: 0, max: 0 },
    description: "Exact match between FSC and Pacific",
  },
  [SeverityTier.ACCEPTABLE]: {
    label: "Acceptable",
    color: "#166534",
    backgroundColor: "#bbf7d0",
    threshold: { min: 0.01, max: 0.5 },
    description: "Within acceptable rounding tolerance",
  },
  [SeverityTier.MINOR]: {
    label: "Minor",
    color: "#a16207",
    backgroundColor: "#fef9c3",
    threshold: { min: 0.51, max: 2.0 },
    description: "Small variance requiring review",
  },
  [SeverityTier.VARIANCE]: {
    label: "Variance",
    color: "#c2410c",
    backgroundColor: "#fed7aa",
    threshold: { min: 2.01, max: 10.0 },
    description: "Significant variance requiring investigation",
  },
  [SeverityTier.CRITICAL]: {
    label: "Critical",
    color: "#991b1b",
    backgroundColor: "#fecaca",
    threshold: { min: 10.01, max: Infinity },
    description: "Critical variance requiring immediate attention",
  },
  [SeverityTier.ERROR]: {
    label: "Error",
    color: "#7f1d1d",
    backgroundColor: "#fca5a5",
    threshold: { min: -1, max: -1 },
    description: "Processing error occurred",
  },
  [SeverityTier.SKIPPED_ZERO]: {
    label: "Skipped (Zero)",
    color: "#6b7280",
    backgroundColor: "#f3f4f6",
    threshold: { min: -1, max: -1 },
    description: "Skipped due to zero pay packet",
  },
  [SeverityTier.SKIPPED_REVERSAL]: {
    label: "Skipped (Reversal)",
    color: "#9ca3af",
    backgroundColor: "#e5e7eb",
    threshold: { min: -1, max: -1 },
    description: "Skipped due to reversal transaction",
  },
  [SeverityTier.SKIPPED_REVERSAL_TYPE]: {
    label: "Skipped (Reversal Type)",
    color: "#9ca3af",
    backgroundColor: "#e5e7eb",
    threshold: { min: -1, max: -1 },
    description: "Skipped due to reversal document type",
  },
  [SeverityTier.SKIPPED_INTERVENTION]: {
    label: "Skipped (Intervention)",
    color: "#78716c",
    backgroundColor: "#f5f5f4",
    threshold: { min: -1, max: -1 },
    description: "Skipped due to manual intervention",
  },
  [SeverityTier.SKIPPED_PRE_CONFIG]: {
    label: "Skipped (Pre-Config)",
    color: "#78716c",
    backgroundColor: "#f5f5f4",
    threshold: { min: -1, max: -1 },
    description: "Skipped due to pre-configuration state",
  },
};

export const SEVERITY_TIER_ORDER: SeverityTier[] = [
  SeverityTier.MATCH,
  SeverityTier.ACCEPTABLE,
  SeverityTier.MINOR,
  SeverityTier.VARIANCE,
  SeverityTier.CRITICAL,
  SeverityTier.ERROR,
  SeverityTier.SKIPPED_ZERO,
  SeverityTier.SKIPPED_REVERSAL,
  SeverityTier.SKIPPED_REVERSAL_TYPE,
  SeverityTier.SKIPPED_INTERVENTION,
  SeverityTier.SKIPPED_PRE_CONFIG,
];
